/**
 * Monthly Collections report API (IPD-MVP discharge-mis, BUILD-PLAN
 * fast-slice-v2 §7 FROZEN API CONTRACT). S2 shipped the board + register
 * GETs. F1 (this pass) adds the export blob download and the full
 * distinct-error-code mapping (§7.4's three 409 refusals).
 *
 * Every route is PERMISSION-gated server-side
 * (`require_permission(VIEW_MONTHLY_COLLECTIONS)` / `EXPORT_MONTHLY_COLLECTIONS`);
 * the FE gate mirrors it via `constants/reports.js`'s
 * `MONTHLY_COLLECTIONS_PERMISSIONS`. The Busy monthly-files routes are the
 * exception: they need `DOWNLOAD_BUSY_FILES` (`BUSY_FILES_PERMISSIONS`, OR-84).
 */

import apiClient from './apiClient';
import { FILE_NOT_FOUND_TEXT } from '../../constants/busyFile';

const BASE = '/api/organisation/reports';

function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  // 401/403 NEVER echo the server's `detail` string. `require_permission`
  // raises a bare engineering string ("Permission 'VIEW_MONTHLY_COLLECTIONS'
  // is required. Your role 'RECEPTIONIST' does not have it.") — a permission
  // slug and an internal role name are not copy for an accountant. The
  // status mapping below owns those two cases. A bare 503 (the board's
  // DynamoDB-failure path) carries FastAPI's default "Service Unavailable"
  // string — also not copy, so the 503 mapping below owns it too.
  const status = error?.response?.status;
  if (typeof detail === 'string' && status !== 401 && status !== 403 && status !== 503) {
    return { error: detail };
  }
  switch (status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to view this report.' };
    case 422: return { error: 'That month is invalid.', code: 'INVALID_PERIOD' };
    case 503: return { error: 'Reports are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    default:  return { error: fallback };
  }
}

/**
 * Export-specific error parsing. `responseType: 'blob'` means axios delivers
 * ANY error body (including the §7.4 structured 409s) as a `Blob`, not
 * parsed JSON — `parseError` above can't see `detail.code` on it. Read the
 * blob as text ourselves first (same `FileReader` idiom as
 * `admissionDischargeApi.openLabBillPdf` — jsdom's test `Blob` has no
 * `.text()`), then fall back to the generic status mapping. Every §7.4 code
 * (`REPORT_INCOMPLETE` / `REPORT_UNRECONCILED` / `REPORT_NO_CONTENT` /
 * `IPD_MODULE_DISABLED` / `INVALID_PERIOD` / `IPD_TABLE_UNAVAILABLE`) is
 * forwarded VERBATIM as the FE `code` — passthrough, never remapped, so each
 * one stays distinct by construction.
 */
async function parseExportError(error, fallback) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(data);
      });
      const parsed = JSON.parse(text);
      const detail = parsed?.detail;
      if (detail && typeof detail === 'object' && detail.code) {
        const { code, message, ...extras } = detail;
        return { error: message || fallback, code, ...extras };
      }
    } catch (_parseErr) {
      // Not JSON / unreadable blob — fall through to the generic mapping.
    }
  }
  switch (status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to export this report.' };
    case 409: return { error: 'This month cannot be exported right now.', code: 'REPORT_NOT_EXPORTABLE' };
    case 422: return { error: 'That month is invalid.', code: 'INVALID_PERIOD' };
    case 503: return { error: 'Reports are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    default:  return { error: fallback };
  }
}

/**
 * GET /reports/monthly-collections?month=YYYY-MM — the board. `month`
 * omitted ⇒ server defaults to the last full org-local IST month (C5,
 * `resolve_period()`). Response passed through verbatim — additive fields
 * are safe (§7.0).
 */
export const getMonthlyCollections = async (month) => {
  try {
    const response = await apiClient.get(`${BASE}/monthly-collections`, { params: month ? { month } : {} });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[MonthlyReportsAPI] getMonthlyCollections error:', error);
    return { success: false, ...parseError(error, 'Failed to load the collections report.') };
  }
};

/**
 * GET /reports/discharges?month=YYYY-MM — the month-wise discharge register
 * (§7.2). Callers must only invoke this when the board's `ipd` key is
 * present — an OPD-only org 403s (`IPD_MODULE_DISABLED`) by design.
 */
export const getDischargeRegister = async (month) => {
  try {
    const response = await apiClient.get(`${BASE}/discharges`, { params: month ? { month } : {} });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[MonthlyReportsAPI] getDischargeRegister error:', error);
    return { success: false, ...parseError(error, 'Failed to load the discharge register.') };
  }
};

// Single-flight guard (§ Runtime safety — no request storms). One export
// download in flight at a time; a second call while one is pending reuses
// the SAME promise instead of firing a second GET.
let exportInFlight = null;

/**
 * GET /reports/monthly-collections/export?month=YYYY-MM (§7.4) — the
 * workbook. Same blob + in-app `useFileViewerStore` pattern as
 * `ipdReportsApi.openIpdDepositsReportPdf` / `admissionDischargeApi.
 * openInterimInvoicePdf` — never a raw `window.open`. Refused months (409
 * REPORT_INCOMPLETE|REPORT_UNRECONCILED|REPORT_NO_CONTENT) produce no file;
 * the caller reads `code` to show the right refusal copy.
 */
export const downloadMonthlyCollectionsExport = (month) => {
  if (exportInFlight) return exportInFlight;

  exportInFlight = (async () => {
    try {
      const response = await apiClient.get(`${BASE}/monthly-collections/export`, {
        params: month ? { month } : {},
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const blobUrl = URL.createObjectURL(blob);
      // Matches the backend's filename (§7.4 amended) — "Collections" is a
      // user-visible string the product has ruled out (M1), and this name
      // reaches the user via the browser's save dialog / downloads list.
      const filename = `reports-${month || 'export'}.xlsx`;
      const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
      useFileViewerStore.getState().openViewer({ blobUrl, fileType: 'xlsx', title: filename });
      return { success: true, blobUrl, filename };
    } catch (error) {
      console.error('[MonthlyReportsAPI] downloadMonthlyCollectionsExport error:', error);
      return { success: false, ...(await parseExportError(error, 'Failed to export this month.')) };
    } finally {
      exportInFlight = null;
    }
  })();

  return exportInFlight;
};

// ── Report archive (reports-archive phase, FROZEN CONTRACT C5) ──────────
// Two additive reads on the SAME router/file idiom. No blob, no
// `responseType`, no `useFileViewerStore` (C6 — the archive downloads via a
// presigned S3 URL + programmatic anchor, never fetched through the app).
// Error copy is mapped HERE (same convention as `parseError` above) but per
// C3's card-vs-row split, since the two calls render on different surfaces
// with different PO strings for the same status code.

function parseArchiveListError(error, fallback) {
  // C3: only two card-level strings exist — 403 vs "everything else" — the
  // backend's `detail.message` is never surfaced verbatim here (unlike the
  // download 404 case, the list card has no per-code copy to pick between).
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    return { error: fallback, code: detail.code };
  }
  const status = error?.response?.status;
  switch (status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: "You don't have access to this report." };
    default:  return { error: fallback };
  }
}

// R3 download copy (R3-copy-review E4–E7). `DOWNLOAD_RECORD_UNAVAILABLE` (the
// per-download record couldn't be saved — OR-78: the download is BLOCKED, no
// URL is minted) and `REPORT_ARCHIVE_UNAVAILABLE` (file store down) read the
// same to the user: try again later.
const DOWNLOAD_UNAVAILABLE_COPY = 'Downloads are temporarily unavailable. Try again.';
// The card re-reads the list on a 404 and words the outcome itself (it can say
// "refreshed" only once that re-read worked), so this is the plain not-found.
const DOWNLOAD_NOT_FOUND_COPY = FILE_NOT_FOUND_TEXT;
const DOWNLOAD_FAILED_COPY = "Couldn't prepare the file. Try again.";

const DOWNLOAD_ERROR_COPY = {
  REPORT_NOT_AVAILABLE: DOWNLOAD_NOT_FOUND_COPY,
  DOWNLOAD_RECORD_UNAVAILABLE: DOWNLOAD_UNAVAILABLE_COPY,
  REPORT_ARCHIVE_UNAVAILABLE: DOWNLOAD_UNAVAILABLE_COPY,
};

function parseArchiveDownloadError(error, fallback) {
  // Row-level copy per `detail.code`; any other coded error falls back to the
  // generic "couldn't prepare" copy. Backend `detail.message` prose is never
  // surfaced (FE maps on code, not prose).
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const known = Object.prototype.hasOwnProperty.call(DOWNLOAD_ERROR_COPY, detail.code);
    return { error: known ? DOWNLOAD_ERROR_COPY[detail.code] : fallback, code: detail.code };
  }
  const status = error?.response?.status;
  switch (status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: "You don't have access to this report." };
    default:  return { error: fallback };
  }
}

/**
 * GET /api/organisation/reports/archive — the org's own archive listing
 * (C1). No params, ever — `org_id` comes from the auth context server-side.
 * One call per page mount (C7); no single-flight guard needed here (the
 * caller/card owns the mount-once ref, same idiom as this page's board
 * fetch).
 */
export const getReportArchive = async () => {
  try {
    const response = await apiClient.get(`${BASE}/archive`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[MonthlyReportsAPI] getReportArchive error:', error);
    return { success: false, ...parseArchiveListError(error, "Couldn't load the report archive. Try again.") };
  }
};

/**
 * GET /api/organisation/reports/archive/{period}/{kind}/download (C2) —
 * mints a presigned URL, valid `expires_in_seconds` (always 300). The
 * caller navigates to `url` via a programmatic anchor (C6) — this function
 * never fetches the bytes. Guarded by the calling row's own busy state, not
 * a module-level single-flight (each row is independent).
 */
export const getReportArchiveDownloadUrl = async (period, kind) => {
  try {
    const response = await apiClient.get(`${BASE}/archive/${period}/${kind}/download`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[MonthlyReportsAPI] getReportArchiveDownloadUrl error:', error);
    return { success: false, ...parseArchiveDownloadError(error, DOWNLOAD_FAILED_COPY) };
  }
};

// ── Monthly files (BUSY-VOUCHER R3, FROZEN CONTRACT §2.1) ───────────────
// ONE read that lists every month's Busy file state + the books status block.
// `org_id` comes from the auth context server-side — no params, ever. The
// response is passed through VERBATIM (`books` may be null when the status
// read failed; `books_status_error` says so). Row/status copy lives in
// `constants/busyFile.js`, never here.
const MONTHLY_FILES_LOAD_ERROR = "Couldn't load the monthly files. Try again.";

// Single-flight (§ Runtime safety — no request storms): callers that overlap
// (a double mount, a refetch racing the first load) share ONE in-flight GET.
// Cleared on settle, so a later call (e.g. the refetch after a download 404)
// fires a fresh request.
let monthlyFilesInFlight = null;

/**
 * GET /api/organisation/reports/monthly-files. 403 -> no-access copy,
 * 401 -> session copy, everything else (incl. 503 REPORT_ARCHIVE_UNAVAILABLE)
 * -> the load-error copy; the backend prose is never surfaced.
 */
export const getMonthlyFiles = () => {
  if (monthlyFilesInFlight) return monthlyFilesInFlight;

  monthlyFilesInFlight = (async () => {
    try {
      const response = await apiClient.get(`${BASE}/monthly-files`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('[MonthlyReportsAPI] getMonthlyFiles error:', error);
      return { success: false, ...parseArchiveListError(error, MONTHLY_FILES_LOAD_ERROR) };
    } finally {
      monthlyFilesInFlight = null;
    }
  })();
  return monthlyFilesInFlight;
};

const monthlyReportsApi = {
  getMonthlyCollections,
  getDischargeRegister,
  downloadMonthlyCollectionsExport,
  getReportArchive,
  getReportArchiveDownloadUrl,
  getMonthlyFiles,
};

export default monthlyReportsApi;
